import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import dayjs from "dayjs";
import { RefreshCw } from "lucide-react";
import { toast } from "sonner";
import posthog from "@/lib/posthog";
import { trpc } from "@/trpc";
import { Button } from "@/components/ui/button";
import type { PipelinePosition } from "@/contexts/PipelineContext";
import UserContext from "@/contexts/UserContext";
import { canMoveLeadToPipelinePosition } from "@/utils/permissions";
import { Columna } from "./PositionColumn";
import ProspectoCard from "./ProspectoCard";
import { ListaRows } from "./ListaView";
import StickyScrollRail from "./StickyScrollRail";
import {
  headerColorForStage,
  MOVABLE_STAGES,
  type TableroBoardRow,
  type TableroQueryFilters,
} from "./types";

const PAGE_SIZE = 25;

export type SearchFilters = TableroQueryFilters & { search?: string };

const buildParams = (
  merchantId: string,
  pipelineId: string,
  filters: SearchFilters,
  pipelinePositionId: string | undefined,
  page: number
) => ({
  merchantId,
  pipelineId,
  pipelinePositionId,
  employeeIds: filters.employeeIds,
  source: filters.source,
  leadType: filters.leadType,
  campaignName: filters.campaignName,
  tagIds: filters.tagIds,
  needsReply: filters.needsReply,
  search: filters.search,
  startDate: filters.startDate,
  endDate: filters.endDate,
  page,
  pageSize: PAGE_SIZE,
});

const filtersKeyOf = (filters: SearchFilters): string =>
  [
    filters.startDate,
    filters.endDate,
    filters.source,
    filters.leadType,
    filters.campaignName,
    filters.tagIds?.join(","),
    filters.employeeIds?.join(","),
    filters.needsReply,
    filters.search,
  ].join("|");

// Page-accumulating query against the non-realtime search endpoint; deduped by id so a
// refetch of an already-appended page can't duplicate rows.
const usePagedBoardRows = (
  merchantId: string,
  pipelineId: string,
  filters: SearchFilters,
  pipelinePositionId: string | undefined,
  resetKey: string,
  onFreshness: (key: string, updatedAt: number) => void
) => {
  const [page, setPage] = useState(0);
  const [accumulated, setAccumulated] = useState<TableroBoardRow[]>([]);
  const [hasMore, setHasMore] = useState(false);

  const fullResetKey = `${resetKey}|${pipelineId}|${filtersKeyOf(filters)}`;
  useEffect(() => {
    setPage(0);
    setAccumulated([]);
    setHasMore(false);
  }, [fullResetKey]);

  const query = trpc.leads.searchActiveNonRealtime.useQuery(
    buildParams(merchantId, pipelineId, filters, pipelinePositionId, page),
    {
      // The only `retry` override in this package, and it is deliberate. This query
      // mounts once per Kanban column, so react-query's default retry:3 turns a
      // 14-column board into up to 56 requests against a shared, workspace-wide
      // Tinybird vCPU budget — which is exactly how the 2026-09 Tablero outage
      // extended itself. A rate-limited board must back off, not push harder.
      retry: 1,
      // Volatile data: 60_000 per the house convention (CreateSendModal.tsx:127,137).
      // Reference data uses 5 * 60 * 1000.
      staleTime: 60_000,
    }
  );

  useEffect(() => {
    if (!query.data) return;
    const rows = query.data.data;
    setHasMore(rows.length === PAGE_SIZE);
    if (page === 0) {
      setAccumulated(rows);
    } else {
      setAccumulated((prev) => {
        const seen = new Set(prev.map((r) => r.id));
        return [...prev, ...rows.filter((r) => !seen.has(r.id))];
      });
    }
  }, [query.data, page]);

  const freshnessKey = pipelinePositionId ?? "__lista__";
  useEffect(() => {
    if (query.dataUpdatedAt) onFreshness(freshnessKey, query.dataUpdatedAt);
  }, [query.dataUpdatedAt, freshnessKey, onFreshness]);

  return {
    rows: accumulated,
    isLoading: query.isLoading && accumulated.length === 0,
    // A failed refetch that still has accumulated pages keeps showing them; the
    // error branch only takes over when the column has nothing to render.
    isError: query.isError && accumulated.length === 0,
    isFetching: query.isFetching,
    hasMore,
    loadMore: () => setPage((p) => p + 1),
  };
};

const SearchColumn = ({
  merchantId,
  pipelineId,
  position,
  filters,
  resetKey,
  draggable,
  droppable,
  onSelectLead,
  selectedLeadId,
  employeeNameById,
  onFreshness,
}: {
  merchantId: string;
  pipelineId: string;
  position: PipelinePosition;
  filters: SearchFilters;
  resetKey: string;
  draggable: boolean;
  droppable: boolean;
  onSelectLead: (leadId: string) => void;
  selectedLeadId?: string;
  employeeNameById: Map<string, string>;
  onFreshness: (key: string, updatedAt: number) => void;
}) => {
  const { rows, isLoading, isError, isFetching, hasMore, loadMore } = usePagedBoardRows(
    merchantId,
    pipelineId,
    filters,
    position.id,
    resetKey,
    onFreshness
  );

  return (
    <Columna
      title={position.displayName}
      accent={headerColorForStage(position.stage)}
      countLabel={`${rows.length}${hasMore ? "+" : ""}`}
      droppableId={`position:${position.id}`}
      droppableDisabled={!droppable}
      isLoading={isLoading}
      isError={isError}
      itemIds={rows.map((r) => r.id)}
      hasMore={hasMore}
      loadingMore={isFetching}
      onLoadMore={loadMore}
    >
      {rows.map((lead) => (
        <ProspectoCard
          key={lead.id}
          lead={lead}
          selected={selectedLeadId === lead.id}
          draggable={draggable}
          onClick={() => onSelectLead(lead.id)}
          employeeNameById={employeeNameById}
        />
      ))}
    </Columna>
  );
};

const SearchLista = ({
  merchantId,
  pipelineId,
  filters,
  etapa,
  resetKey,
  positionById,
  onSelectLead,
  selectedLeadId,
  employeeNameById,
  onFreshness,
}: {
  merchantId: string;
  pipelineId: string;
  filters: SearchFilters;
  etapa?: string;
  resetKey: string;
  positionById: Map<string, PipelinePosition>;
  onSelectLead: (leadId: string) => void;
  selectedLeadId?: string;
  employeeNameById: Map<string, string>;
  onFreshness: (key: string, updatedAt: number) => void;
}) => {
  const { rows, isLoading, isError, isFetching, hasMore, loadMore } = usePagedBoardRows(
    merchantId,
    pipelineId,
    filters,
    etapa,
    resetKey,
    onFreshness
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1">
        <ListaRows
          rows={rows}
          positionById={positionById}
          employeeNameById={employeeNameById}
          onSelectLead={onSelectLead}
          selectedLeadId={selectedLeadId}
          isLoading={isLoading}
          isError={isError}
        />
      </div>
      {hasMore ? (
        <div className="border-t border-neutral-100 p-2">
          <button
            onClick={loadMore}
            disabled={isFetching}
            className="min-h-11 w-full rounded-md border border-neutral-300 bg-surface text-xs font-medium text-neutral-600 transition-colors hover:bg-neutral-100 disabled:opacity-50"
          >
            {isFetching ? "Cargando…" : "Cargar más"}
          </button>
        </div>
      ) : null}
    </div>
  );
};

const NonRealtimeSearchBoard = ({
  merchantId,
  pipelineId,
  positions,
  filters,
  vista,
  etapa,
  positionById,
  onSelectLead,
  selectedLeadId,
  employeeNameById,
}: {
  merchantId: string;
  pipelineId: string;
  positions: PipelinePosition[];
  filters: SearchFilters;
  vista: "kanban" | "lista";
  // Etapa filter for the lista query (kanban narrows via `positions`).
  etapa?: string;
  positionById: Map<string, PipelinePosition>;
  onSelectLead: (leadId: string) => void;
  selectedLeadId?: string;
  employeeNameById: Map<string, string>;
}) => {
  const utils = trpc.useUtils();
  const { email } = useContext(UserContext);
  const moveMutation = trpc.leads.movePipelinePosition.useMutation();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Bumping the key resets every column to page 0 before the refetch, so the
  // page-accumulation state can't duplicate or interleave stale pages.
  const [refreshKey, setRefreshKey] = useState(0);
  const handleRefresh = () => {
    setRefreshKey((k) => k + 1);
    utils.leads.searchActiveNonRealtime.invalidate();
  };

  // Staleness of the most-stale mounted query, ticking every 30s.
  const [freshnessByKey, setFreshnessByKey] = useState<Map<string, number>>(new Map());
  const handleFreshness = useCallback((key: string, updatedAt: number) => {
    setFreshnessByKey((prev) => {
      if (prev.get(key) === updatedAt) return prev;
      const next = new Map(prev);
      next.set(key, updatedAt);
      return next;
    });
  }, []);
  const [, setTick] = useState(0);
  useEffect(() => {
    const interval = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(interval);
  }, []);
  const oldestUpdatedAt = useMemo(() => {
    const values = Array.from(freshnessByKey.values());
    return values.length ? Math.min(...values) : undefined;
  }, [freshnessByKey]);

  const positionMetaById = useMemo(() => {
    const map = new Map<string, { stage: string; displayName: string }>();
    for (const p of positions) map.set(p.id, { stage: p.stage, displayName: p.displayName });
    return map;
  }, [positions]);

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const leadId = String(active.id);
    const sourcePositionId = active.data.current?.pipelinePositionId as string | undefined;
    const overId = String(over.id);
    if (!overId.startsWith("position:")) return;
    const targetPositionId = overId.slice("position:".length);
    if (!sourcePositionId || sourcePositionId === targetPositionId) return;

    const sourceMeta = positionMetaById.get(sourcePositionId);
    const targetMeta = positionMetaById.get(targetPositionId);
    if (!sourceMeta || !targetMeta) return;

    if (!MOVABLE_STAGES.has(sourceMeta.stage) || !MOVABLE_STAGES.has(targetMeta.stage)) {
      toast.error("No puedes mover leads desde o hacia las columnas de WhatsApp.");
      return;
    }

    // Belt to the droppable={} braces below: a fianza column is not a drop target for a
    // merchant's own advisors, and the endpoint rejects the move either way.
    if (!canMoveLeadToPipelinePosition({ email, pipelinePositionId: targetPositionId })) {
      toast.error("No autorizado");
      return;
    }

    // The non-realtime store is CDC-lagged: no cache surgery, just persist + invalidate.
    try {
      await moveMutation.mutateAsync({
        merchantId,
        leadId,
        pipelinePositionId: targetPositionId,
      });
      toast.success(
        `Lead movido a ${targetMeta.displayName}. El tablero sin tiempo real puede tardar en reflejarlo.`
      );
      posthog.capture("tablero lead moved", {
        merchant_id: merchantId,
        lead_id: leadId,
        pipeline_id: pipelineId,
        source_position_id: sourcePositionId,
        target_position_id: targetPositionId,
        target_position_name: targetMeta.displayName,
        via: "search",
      });
      utils.leads.searchActiveNonRealtime.invalidate();
    } catch (error) {
      toast.error("No pudimos mover el lead. Intenta de nuevo.");
      console.error("movePipelinePosition failed", error);
    }
  };

  const resetKey = `${refreshKey}`;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex min-h-9 items-center justify-end gap-2 border-b border-neutral-100 bg-surface px-4 py-1">
        <span className="text-xs text-neutral-600">
          Datos no en tiempo real
          {oldestUpdatedAt ? ` · Actualizado ${dayjs(oldestUpdatedAt).locale("es").fromNow()}` : ""}
        </span>
        <Button variant="ghost" size="sm" className="h-8 text-neutral-600" onClick={handleRefresh}>
          <RefreshCw className="mr-1 h-3.5 w-3.5" /> Refrescar
        </Button>
      </div>
      {vista === "kanban" ? (
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <StickyScrollRail className="flex min-h-0 flex-1 gap-3 p-4">
            {positions.map((position) => {
              const movable = MOVABLE_STAGES.has(position.stage);
              // Leads may always leave a fianza column, so only the drop side is gated.
              const droppable =
                movable &&
                canMoveLeadToPipelinePosition({ email, pipelinePositionId: position.id });
              return (
                <SearchColumn
                  key={position.id}
                  merchantId={merchantId}
                  pipelineId={pipelineId}
                  position={position}
                  filters={filters}
                  resetKey={resetKey}
                  draggable={movable}
                  droppable={droppable}
                  onSelectLead={onSelectLead}
                  selectedLeadId={selectedLeadId}
                  employeeNameById={employeeNameById}
                  onFreshness={handleFreshness}
                />
              );
            })}
          </StickyScrollRail>
        </DndContext>
      ) : (
        <SearchLista
          merchantId={merchantId}
          pipelineId={pipelineId}
          filters={filters}
          etapa={etapa}
          resetKey={resetKey}
          positionById={positionById}
          onSelectLead={onSelectLead}
          selectedLeadId={selectedLeadId}
          employeeNameById={employeeNameById}
          onFreshness={handleFreshness}
        />
      )}
    </div>
  );
};

export default NonRealtimeSearchBoard;
